import { z } from 'zod';

export const ThemeColorItemSchema = z.object({
  color: z.string().describe('The color to use for the theme.'),
  name: z.string().describe('The name of the color.'),
});

export type ThemeColorItem = z.infer<typeof ThemeColorItemSchema>;

export const ThemeFontVariantSchema = z.object({
  url: z.string().describe('The URL of the font family.'),
  weight: z.string().describe('The weight of the font family.'),
  style: z.string().describe('The style of the font family.'),
});

export type ThemeFontVariant = z.infer<typeof ThemeFontVariantSchema>;

export const ThemeFontFamilyItemSchema = z.object({
  family: z.string().describe('The font family to use for the theme.'),
  name: z.string().describe('The name of the font family.'),
  variants: z.array(ThemeFontVariantSchema).describe('The variants of the font family.'),
});

// Composable theme schema definitions
export const ThemeBreakpointsSchema = z.object({
  'breakpoints.sm': z.string().default('40rem').describe('The small breakpoint for the theme.'),
  'breakpoints.md': z.string().default('48rem').describe('The medium breakpoint for the theme.'),
  'breakpoints.lg': z.string().default('64rem').describe('The large breakpoint for the theme.'),
});

export const ThemeShadowSchema = z.object({
  'shadow.sm': z.string().default('0 1px 2px 0 rgb(0 0 0 / 0.05)').describe('Small box shadow for the theme.'),
  'shadow.md': z
    .string()
    .default('0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)')
    .describe('Medium box shadow for the theme.'),
  'shadow.lg': z
    .string()
    .default('0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)')
    .describe('Large box shadow for the theme.'),
  'shadow.xl': z
    .string()
    .default('0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)')
    .describe('Extra large box shadow for the theme.'),
  'shadow.2xl': z.string().default('0 25px 50px -12px rgb(0 0 0 / 0.25)').describe('2XL box shadow for the theme.'),
});

export enum ThemeColorName {
  Primary = 'Primary',
  Secondary = 'Secondary',
  Tertiary = 'Tertiary',
  Dark = 'Dark',
  Light = 'Light',
}

export enum ThemeColorKey {
  Primary = 'colors.primary',
  Secondary = 'colors.secondary',
  Tertiary = 'colors.tertiary',
  Dark = 'colors.dark',
  Light = 'colors.light',
  Additional = 'colors.additional',
}

export const ThemeColorsSchema = z.object({
  [ThemeColorKey.Primary]: ThemeColorItemSchema.default({
    color: 'oklch(62.3% 0.214 259.815)', // Tailwind indigo-600
    name: ThemeColorName.Primary,
  }).describe(
    'Primary brand color for buttons, links, focus states, and key interactive elements. Flows to CSS variable --color-primary. Used by primary variant components and CTA elements',
  ),
  [ThemeColorKey.Secondary]: ThemeColorItemSchema.default({
    color: 'oklch(55.4% 0.046 257.417)', // Tailwind slate-600
    name: ThemeColorName.Secondary,
  }).describe(
    'Secondary color for supporting elements, secondary buttons, and subtle accents. Flows to CSS variable --color-secondary. Used by secondary variant components and supporting UI elements',
  ),
  [ThemeColorKey.Tertiary]: ThemeColorItemSchema.default({
    color: 'oklch(70.5% 0.213 146.604)', // Tailwind emerald-500
    name: ThemeColorName.Tertiary,
  }).describe(
    'Tertiary accent color for highlights, success states, and special emphasis. Flows to CSS variable --color-tertiary. Used by tertiary variant components and accent elements',
  ),
  [ThemeColorKey.Dark]: ThemeColorItemSchema.default({
    color: 'oklch(20.8% 0.042 265.755)', // Tailwind slate-900
    name: ThemeColorName.Dark,
  }).describe(
    'Dark color for dark backgrounds and high-contrast elements. Used by dark theme components and contrasting sections',
  ),
  [ThemeColorKey.Light]: ThemeColorItemSchema.default({
    color: 'oklch(98.5% 0 0)', // Tailwind white
    name: ThemeColorName.Light,
  }).describe(
    'Light color for light backgrounds and low-contrast elements. Used by light theme components and contrasting sections',
  ),
  [ThemeColorKey.Additional]: z
    .array(ThemeColorItemSchema)
    .optional()
    .default([])
    .describe('Additional colors for the theme.'),
});

export const ThemeFontFamilySchema = z.object({
  fonts: z
    .array(ThemeFontFamilyItemSchema)
    .default([
      {
        family:
          'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
        name: 'Sans',
        variants: [
          {
            url: '',
            weight: 'normal',
            style: 'normal',
          },
          {
            url: '',
            weight: 'bold',
            style: 'normal',
          },
        ],
      },
      {
        family: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
        name: 'Serif',
        variants: [
          {
            url: '',
            weight: 'normal',
            style: 'normal',
          },
          {
            url: '',
            weight: 'bold',
            style: 'normal',
          },
        ],
      },
      {
        family: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        name: 'Mono',
        variants: [
          {
            url: '',
            weight: 'normal',
            style: 'normal',
          },
          {
            url: '',
            weight: 'bold',
            style: 'normal',
          },
        ],
      },
    ])
    .describe('The font families to use for the theme.'),
});

const ButtonBaseVariantSchema = z
  .object({
    backgroundColor: z.string().optional(),
    color: z.string().optional(),
    padding: z.string().optional(),
    borderRadius: z.string().optional(),
    border: z.string().optional(),
    fontFamily: z.string().optional(),
    fontSize: z.string().optional(),
    lineHeight: z.string().optional(),
    letterSpacing: z.string().optional(),
    fontWeight: z.string().optional(),
    transition: z.string().optional(),
    textDecoration: z.string().optional(),
  })
  .partial();

const ButtonHoverVariantSchema = z
  .object({
    backgroundColor: z.string().optional(),
    color: z.string().optional(),
    opacity: z.string().optional(),
  })
  .partial();

export const ButtonVariantsSchema = z
  .object({
    base: ButtonBaseVariantSchema,
    hover: ButtonHoverVariantSchema,
  })
  .describe('The CSS properties for a button variant.');

export type ThemeButtonVariant = z.infer<typeof ButtonVariantsSchema>;

function getButtonVariantSchemaWithDefaults({
  base = {},
  hover = {},
}: {
  base?: Partial<z.infer<typeof ButtonBaseVariantSchema>>;
  hover?: Partial<z.infer<typeof ButtonHoverVariantSchema>>;
}) {
  return z.object({
    base: ButtonBaseVariantSchema.default(base),
    hover: ButtonHoverVariantSchema.default(hover),
  });
}

export enum ThemeButtonVariantKey {
  Primary = 'variants.button.primary',
  Secondary = 'variants.button.secondary',
  Tertiary = 'variants.button.tertiary',
  Outline = 'variants.button.outline',
  Ghost = 'variants.button.ghost',
  Link = 'variants.button.link',
}

export function isButtonVariantKey(key: string): key is ThemeButtonVariantKey {
  return Object.values(ThemeButtonVariantKey).includes(key as ThemeButtonVariantKey);
}

export function isTextVariantKey(key: string): key is ThemeTextVariantKey {
  return Object.values(ThemeTextVariantKey).includes(key as ThemeTextVariantKey);
}

export function isVariantKey(key: string): key is ThemeButtonVariantKey | ThemeTextVariantKey {
  return isButtonVariantKey(key) || isTextVariantKey(key);
}

export const ThemeButtonVariantsSchema = z
  .object({
    [ThemeButtonVariantKey.Primary]: ButtonVariantsSchema.describe('The CSS properties for primary button variant.'),
    [ThemeButtonVariantKey.Secondary]: getButtonVariantSchemaWithDefaults({
      base: {
        backgroundColor: 'bg-secondary',
      },
      hover: {
        backgroundColor: 'bg-tertiary',
      },
    }).describe('The CSS properties for secondary button variant.'),
    [ThemeButtonVariantKey.Tertiary]: getButtonVariantSchemaWithDefaults({
      base: {
        backgroundColor: 'bg-tertiary',
      },
      hover: {
        backgroundColor: 'bg-primary',
        color: 'text-secondary',
      },
    }).describe('The CSS properties for tertiary button variant.'),
    [ThemeButtonVariantKey.Outline]: getButtonVariantSchemaWithDefaults({
      base: {
        backgroundColor: undefined,
      },
      hover: {
        backgroundColor: 'bg-light',
      },
    }).describe('The CSS properties for outline button variant.'),
    [ThemeButtonVariantKey.Ghost]: getButtonVariantSchemaWithDefaults({
      base: {
        border: undefined,
        backgroundColor: undefined,
      },
      hover: {
        backgroundColor: 'bg-light',
      },
    }).describe('The CSS properties for ghost button variant.'),
    [ThemeButtonVariantKey.Link]: z
      .object({
        base: z.object({
          color: z.string().optional(),
          fontFamily: z.string().optional(),
          textDecoration: z.string().optional(),
          transition: z.string().optional(),
        }),
        hover: z.object({
          opacity: z.string().optional(),
        }),
      })
      .partial()
      .describe('The CSS properties for link button variant.'),
  })
  .describe('The CSS properties for button variants.');

export const ThemeTextVariantSchema = z
  .object({
    fontFamily: z.string().optional(),
    fontWeight: z.string().optional(),
    fontSize: z.string().optional(),
    lineHeight: z.string().optional(),
    letterSpacing: z.string().optional(),
    textTransform: z.string().optional(),
  })
  .partial();

export enum ThemeTextVariantKey {
  Body = 'variants.text.body',
  Heading1 = 'variants.text.heading1',
  Heading2 = 'variants.text.heading2',
  Heading3 = 'variants.text.heading3',
  Heading4 = 'variants.text.heading4',
  Overline = 'variants.text.overline',
}

export const ThemeTextVariantsSchema = z.object({
  [ThemeTextVariantKey.Body]: ThemeTextVariantSchema.default({
    fontWeight: 'font-normal',
    fontSize: 'text-base',
    lineHeight: 'leading-normal',
    letterSpacing: 'tracking-wide',
  }).describe('The CSS properties for body text variant.'),
  [ThemeTextVariantKey.Heading1]: ThemeTextVariantSchema.default({
    fontWeight: 'font-bold',
    fontSize: 'text-4xl',
    lineHeight: 'leading-tight',
  }).describe('The CSS properties for heading1 text variant.'),
  [ThemeTextVariantKey.Heading2]: ThemeTextVariantSchema.default({
    fontWeight: 'font-semibold',
    fontSize: 'text-3xl',
    lineHeight: 'leading-tight',
  }).describe('The CSS properties for heading2 text variant.'),
  [ThemeTextVariantKey.Heading3]: ThemeTextVariantSchema.default({
    fontWeight: 'font-semibold',
    fontSize: 'text-2xl',
    lineHeight: 'leading-tight',
  }).describe('The CSS properties for heading3 text variant.'),
  [ThemeTextVariantKey.Heading4]: ThemeTextVariantSchema.default({
    fontWeight: 'font-bold',
    fontSize: 'text-4xl',
    lineHeight: 'leading-tight',
  }).describe('The CSS properties for heading4 text variant.'),
  [ThemeTextVariantKey.Overline]: ThemeTextVariantSchema.default({
    fontWeight: 'font-medium',
    fontSize: 'text-sm',
    lineHeight: 'leading-tight',
    letterSpacing: 'tracking-wider',
    textTransform: 'uppercase',
  }).describe('The CSS properties for overline text variant.'),
});

export enum ThemeLogoKey {
  Light = 'logo.light',
  Dark = 'logo.dark',
}

export type ThemeTextVariant = z.infer<typeof ThemeTextVariantSchema>;

export const ThemeLogosSchema = z.object({
  [ThemeLogoKey.Light]: z.string().optional().describe('The light logo for the theme.'),
  [ThemeLogoKey.Dark]: z.string().optional().describe('The dark logo for the theme.'),
});

export const ThemeVariantsSchema = ThemeButtonVariantsSchema.merge(ThemeTextVariantsSchema);

// Flattened merged schema
export const ThemePropsSchema = ThemeBreakpointsSchema.merge(ThemeShadowSchema)
  .merge(ThemeColorsSchema)
  .merge(ThemeFontFamilySchema)
  .merge(ThemeVariantsSchema)
  .merge(ThemeLogosSchema)
  .partial();

export type ThemeProps = z.infer<typeof ThemePropsSchema>;
